import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ClipboardText, CaretRight, CaretDown, UsersThree, IdentificationCard, ChalkboardTeacher } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { KidAvatar } from "@/components/KidAvatar";

const SHORTCUTS = [
  { key: "hafalan", label: "Input Hafalan", desc: "Tahfidz & progress murid", icon: BookOpen, path: "/kelola/hafalan", color: "bg-amber-100 text-amber-600" },
  { key: "laporan", label: "Input Laporan Harian", desc: "Catatan harian per murid", icon: ClipboardText, path: "/kelola/laporan", color: "bg-emerald-100 text-emerald-600" },
];

export default function KegiatanGuru() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(null); // active sub-kelas
  const [students, setStudents] = useState([]);

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  const toggle = (kelas) => {
    if (open === kelas) { setOpen(null); return; }
    setOpen(kelas); setStudents([]);
    api.get(`/students?kelas=${encodeURIComponent(kelas)}`).then((r) => setStudents(r.data)).catch(() => {});
  };

  const groups = [...new Set(classes.map((c) => c.group))];

  return (
    <div>
      <PageHeader title="Kegiatan Guru" />
      <div className="px-5 pt-4 pb-10">
        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Data Siswa per Kelas</p>
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g} data-testid={`kg-group-${g}`}>
              <p className="font-heading font-bold text-stone-700 mb-1.5">{g}</p>
              <div className="space-y-1.5">
                {classes.filter((c) => c.group === g).map((c) => (
                  <div key={c.kelas} className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden">
                    <button data-testid={`kg-subkelas-${c.kelas}`} onClick={() => toggle(c.kelas)}
                      className="w-full flex items-center gap-3 p-3.5 active:scale-[0.99] transition-transform">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><UsersThree size={20} weight="duotone" /></div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="font-semibold text-stone-800 text-sm">{c.kelas}</p>
                        <p className="text-stone-400 text-xs">{c.student_count} murid</p>
                      </div>
                      {open === c.kelas ? <CaretDown size={18} className="text-stone-400" /> : <CaretRight size={18} className="text-stone-400" />}
                    </button>
                    {open === c.kelas && (
                      <div className="border-t border-stone-100" data-testid={`kg-students-${c.kelas}`}>
                        {students.length === 0 ? (
                          <p className="text-center text-stone-400 text-xs py-4">Belum ada murid di kelas ini.</p>
                        ) : (() => {
                          const teachers = c.teachers || [];
                          const assigned = new Set(teachers.map((t) => t.id));
                          const sections = teachers.map((t) => ({ id: t.id, name: t.name, list: students.filter((s) => s.teacher_id === t.id) }));
                          const unassigned = students.filter((s) => !s.teacher_id || !assigned.has(s.teacher_id));
                          if (unassigned.length) sections.push({ id: "none", name: "Belum ada Umi Guru", list: unassigned });
                          return sections.map((sec) => (
                            <div key={sec.id} data-testid={`kg-teacher-group-${c.kelas}-${sec.id}`}>
                              <div className="px-3.5 py-2 bg-primary/5 text-xs flex items-center gap-1.5">
                                <ChalkboardTeacher size={14} weight="duotone" className="text-primary shrink-0" />
                                <span className="text-stone-400">Umi Guru:</span>
                                <b className="text-stone-600 truncate">{sec.name}</b>
                                <span className="text-stone-400 ml-auto shrink-0">{sec.list.length} murid</span>
                              </div>
                              <div className="divide-y divide-stone-50">
                                {sec.list.length === 0 ? (
                                  <p className="text-center text-stone-300 text-xs py-3">Belum ada murid.</p>
                                ) : sec.list.map((s) => (
                                  <button key={s.id} data-testid={`kg-student-${s.id}`} onClick={() => navigate(`/siswa/${s.id}`)}
                                    className="w-full flex items-center gap-3 px-3.5 py-2.5 active:bg-stone-50 transition-colors text-left">
                                    <KidAvatar gender={s.gender} size={36} src={s.avatar} />
                                    <span className="flex-1 text-sm font-medium text-stone-700 truncate">{s.name}</span>
                                    <IdentificationCard size={18} className="text-primary shrink-0" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-stone-400 uppercase tracking-wide mt-6 mb-2">Input Cepat</p>
        <div className="grid grid-cols-2 gap-3">
          {SHORTCUTS.map((m) => (
            <button key={m.key} data-testid={`kg-shortcut-${m.key}`} onClick={() => navigate(m.path)}
              className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm active:scale-95 transition-transform text-left">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-2 ${m.color}`}><m.icon size={22} weight="duotone" /></div>
              <p className="font-heading font-semibold text-stone-700 text-sm">{m.label}</p>
              <p className="text-stone-400 text-xs mt-0.5">{m.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
